import { useCallback, useEffect, useState } from 'react';

/**
 * V1.4 scope AA.3 — admin Årspakker editor (lives inside the admin
 * client drawer).
 *
 * CRUD + project-linking against:
 *   GET/POST   /api/admin/clients/:clientId/arspakker
 *   PATCH/DEL  /api/admin/arspakker/:id
 *   GET        /api/admin/clients/:clientId/unlinked-videos
 *   POST       /api/admin/arspakker/:id/projects
 *   DELETE     /api/admin/arspakker/:id/projects/:projectId
 */

interface ArspakkeProject {
  id: string;
  title: string;
  phase: string;
  clickupFolderId: string | null;
  clickupListId: string | null;
}

interface Arspakke {
  id: string;
  clientArspakkeNumber: number;
  totalVideos: number;
  videosDelivered: number;
  yearLabel: string | null;
  startedAt: string | null;
  endedAt: string | null;
  notes: string | null;
  projects: ArspakkeProject[];
}

interface UnlinkedVideo {
  id: string;
  title: string;
  phase: string;
  sold_at: string | null;
}

const INPUT_CLS =
  'rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent';

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function ArspakkerEditor({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<Arspakke[]>([]);
  const [unlinked, setUnlinked] = useState<UnlinkedVideo[]>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [list, unlinkedList] = await Promise.all([
        json<{ rows: Arspakke[] }>(`/api/admin/clients/${clientId}/arspakker`),
        json<{ rows: UnlinkedVideo[] }>(`/api/admin/clients/${clientId}/unlinked-videos`),
      ]);
      setRows(list.rows);
      setUnlinked(unlinkedList.rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-2">Årspakker</h3>
        {!creating ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded border border-line-2 px-2 py-1 text-xs hover:border-line-2"
          >
            + Opret ny
          </button>
        ) : null}
      </div>

      {err ? <p className="text-xs text-status-err">{err}</p> : null}

      {creating ? (
        <CreateArspakkeForm
          clientId={clientId}
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await reload();
          }}
        />
      ) : null}

      {loading && rows.length === 0 ? (
        <p className="text-xs text-ink-2">Henter árspakker…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-2">Ingen årspakker oprettet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((p) => (
            <ArspakkeCard key={p.id} arspakke={p} unlinked={unlinked} onChanged={reload} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CreateArspakkeForm({
  clientId,
  onCancel,
  onCreated,
}: {
  clientId: string;
  onCancel: () => void;
  onCreated: () => Promise<void>;
}) {
  const [totalVideos, setTotalVideos] = useState('10');
  const [yearLabel, setYearLabel] = useState(String(new Date().getFullYear()));
  const [startedAt, setStartedAt] = useState('');
  const [videosDelivered, setVideosDelivered] = useState('0');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await json(`/api/admin/clients/${clientId}/arspakker`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          total_videos: Number(totalVideos),
          videos_delivered: Number(videosDelivered || 0),
          year_label: yearLabel || null,
          started_at: startedAt || null,
        }),
      });
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded border border-line-1 bg-paper-1 p-3 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label>
          <div className="mb-1 uppercase tracking-wide text-ink-2">Total videoer</div>
          <input
            type="number"
            min={1}
            value={totalVideos}
            onChange={(e) => setTotalVideos(e.target.value)}
            className={INPUT_CLS}
          />
        </label>
        <label>
          <div className="mb-1 uppercase tracking-wide text-ink-2">Leveret</div>
          <input
            type="number"
            min={0}
            value={videosDelivered}
            onChange={(e) => setVideosDelivered(e.target.value)}
            className={INPUT_CLS}
          />
        </label>
        <label>
          <div className="mb-1 uppercase tracking-wide text-ink-2">År-label</div>
          <input
            type="text"
            value={yearLabel}
            onChange={(e) => setYearLabel(e.target.value)}
            className={INPUT_CLS}
          />
        </label>
        <label>
          <div className="mb-1 uppercase tracking-wide text-ink-2">Startet</div>
          <input
            type="date"
            value={startedAt}
            onChange={(e) => setStartedAt(e.target.value)}
            className={INPUT_CLS}
          />
        </label>
      </div>
      {err ? <p className="mt-2 text-status-err">{err}</p> : null}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded border border-line-2 px-2 py-1 text-ink-1"
        >
          Annullér
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="rounded bg-accent px-2 py-1 text-white disabled:opacity-50"
        >
          {busy ? 'Opretter…' : 'Opret'}
        </button>
      </div>
    </div>
  );
}

function ArspakkeCard({
  arspakke,
  unlinked,
  onChanged,
}: {
  arspakke: Arspakke;
  unlinked: UnlinkedVideo[];
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [totalVideos, setTotalVideos] = useState(String(arspakke.totalVideos));
  const [videosDelivered, setVideosDelivered] = useState(String(arspakke.videosDelivered));
  const [yearLabel, setYearLabel] = useState(arspakke.yearLabel ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const saveEdit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await json(`/api/admin/arspakker/${arspakke.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          total_videos: Number(totalVideos),
          videos_delivered: Number(videosDelivered),
          year_label: yearLabel || null,
        }),
      });
      setEditing(false);
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm('Slet årspakken? Linked video-projekter bliver afknyttet.')) return;
    setBusy(true);
    try {
      await json(`/api/admin/arspakker/${arspakke.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const unlinkProject = async (projectId: string) => {
    setBusy(true);
    try {
      await json(`/api/admin/arspakker/${arspakke.id}/projects/${projectId}`, {
        method: 'DELETE',
      });
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remaining = Math.max(0, arspakke.totalVideos - arspakke.videosDelivered);

  return (
    <li className="rounded border border-line-1 p-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink-0">
          Årspakke #{arspakke.clientArspakkeNumber}
          {arspakke.yearLabel ? ` (${arspakke.yearLabel})` : ''}
        </span>
        <span className="tabular-nums text-ink-2">
          {arspakke.totalVideos} videoer · {arspakke.videosDelivered} leveret · {remaining} tilbage
        </span>
      </div>
      {err ? <p className="mt-1 text-status-err">{err}</p> : null}

      {editing ? (
        <div className="mt-2 grid grid-cols-3 gap-2">
          <label>
            <div className="mb-1 text-[10px] uppercase text-ink-2">Total</div>
            <input
              type="number"
              min={1}
              value={totalVideos}
              onChange={(e) => setTotalVideos(e.target.value)}
              className={INPUT_CLS}
            />
          </label>
          <label>
            <div className="mb-1 text-[10px] uppercase text-ink-2">Leveret</div>
            <input
              type="number"
              min={0}
              value={videosDelivered}
              onChange={(e) => setVideosDelivered(e.target.value)}
              className={INPUT_CLS}
            />
          </label>
          <label>
            <div className="mb-1 text-[10px] uppercase text-ink-2">År-label</div>
            <input
              type="text"
              value={yearLabel}
              onChange={(e) => setYearLabel(e.target.value)}
              className={INPUT_CLS}
            />
          </label>
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-2">
        {editing ? (
          <>
            <button
              type="button"
              onClick={() => void saveEdit()}
              disabled={busy}
              className="rounded bg-accent px-2 py-1 text-white"
            >
              Gem
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
              className="rounded border border-line-2 px-2 py-1"
            >
              Annullér
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="rounded border border-line-2 px-2 py-1"
            >
              Rediger
            </button>
            <button
              type="button"
              onClick={() => setLinking((v) => !v)}
              disabled={busy}
              className="rounded border border-line-2 px-2 py-1"
            >
              {linking ? 'Skjul linkning' : 'Link projekt'}
            </button>
            <button
              type="button"
              onClick={() => void del()}
              disabled={busy}
              className="rounded border border-status-err/40 px-2 py-1 text-status-err"
            >
              Slet
            </button>
          </>
        )}
      </div>

      {arspakke.projects.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 border-t border-line-1 pt-2">
          {arspakke.projects.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span className="truncate text-ink-1">{p.title}</span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="rounded-full border border-line-2 bg-paper-1 px-2 py-0.5">
                  {p.phase}
                </span>
                <button
                  type="button"
                  onClick={() => void unlinkProject(p.id)}
                  disabled={busy}
                  className="text-ink-2 hover:text-status-err"
                >
                  Afknyt
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {linking ? (
        <LinkProjectsPanel
          arspakkeId={arspakke.id}
          unlinked={unlinked}
          onDone={async () => {
            setLinking(false);
            await onChanged();
          }}
        />
      ) : null}
    </li>
  );
}

function LinkProjectsPanel({
  arspakkeId,
  unlinked,
  onDone,
}: {
  arspakkeId: string;
  unlinked: UnlinkedVideo[];
  onDone: () => Promise<void>;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await json(`/api/admin/arspakker/${arspakkeId}/projects`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ project_ids: Array.from(selected) }),
      });
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (unlinked.length === 0) {
    return (
      <p className="mt-2 border-t border-line-1 pt-2 text-ink-2">
        Alle kundens video-projekter er allerede bundet.
      </p>
    );
  }

  return (
    <div className="mt-2 border-t border-line-1 pt-2">
      <ul className="flex flex-col gap-1">
        {unlinked.map((v) => (
          <li key={v.id}>
            <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-paper-1">
              <input
                type="checkbox"
                checked={selected.has(v.id)}
                onChange={() => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(v.id)) next.delete(v.id);
                    else next.add(v.id);
                    return next;
                  });
                }}
              />
              <span className="truncate">{v.title}</span>
              <span className="ml-auto shrink-0 text-[10px] text-ink-2">{v.phase}</span>
            </label>
          </li>
        ))}
      </ul>
      {err ? <p className="mt-1 text-status-err">{err}</p> : null}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          disabled={busy || selected.size === 0}
          onClick={() => void submit()}
          className="rounded bg-accent px-2 py-1 text-white disabled:opacity-50"
        >
          Bind {selected.size || ''}
        </button>
      </div>
    </div>
  );
}
